<template>
  <div>
    <list-header :title="`Outlet: ${getBranchNameById(selectedBranch.id)}`" />

    <!-- Booking Item -->
    <expandable-list
      class="m-3 scss-round-container mt-4"
      :border="service.promotion"
      @click="viewItemDetail(service)">
      <expandable-list-header
        v-if="service.promotion"
        :content="getPromotionHeaderAlt(service)"/>
      <expandable-list-item
        :imgUrl="service.imageURL"
        :name="service.name"
        :processingTime="formatProcessingTime(service.type, service.processingTime)"
        :description="service.description"
        :originalPrice="service.originalPrice"
        :salePrice="service.salePrice"
        :hidePrice="init.hideServicePrice"
        :clickable="false"
      />
    </expandable-list>

    <!-- Select Appointment Date -->
    <sub-header title="Select Date" />
    <Datepicker
      v-model="selectedDate"
      :enableTimePicker="false"
      inline
      auto-apply
      hide-offset-dates
      :allowed-dates="service.promotion ? promotionServiceBranchBookingDateRange : branchBookingDateRange"
      :month-change-on-scroll="false"
      menu-class-name="dp-custom-menu"
      calendar-cell-class-name="dp-custom-cell"
      class="justify-content-center mt-2 mb-2"
    />
    <div class="d-flex flex-row text-start ms-4 mb-3">
      <i class="bi bi-circle-fill my-auto style-avalable-icon" />
      <span class="ms-2 my-auto scss-large-font"> Selected Date </span>
    </div>

    <!-- Select Staff -->
    <!-- <sub-header title="Select Staff and Time" /> -->
    <div class="d-flex flex-row text-center style-vertical-scrolling ms-4">
      <template v-for="employee in employeeTimeslots" :key="employee.id">
        <div
          v-if="employee.timeslots.length"
          class="scss-click-effect rounded-3 me-2 mb-2 style-staff-container"
          :style="
            selectedEmployee?.id === employee?.id
              ? 'background-color:#579eff'
              : ''
          "
          @click="selectedEmployee = employee"
        >
          <smart-image
            :imageURL="employee.image"
            class="rounded-circle"
            style="width: 80px; height: 80px"
          />
          <span
            class="mt-2 scss-overflow-text-style"
            :style="
              selectedEmployee?.id === employee?.id ? 'color:#ffffff' : ''
            "
          >
            {{ employee.firstName }}
          </span>
        </div>
      </template>
    </div>
    <div class="d-flex justify-content-center py-2" v-if="!isEmployeeAvailable">
      No available staff / fully booked.
    </div>
    <!-- Select Time -->
    <div v-if="selectedEmployee">
      <time-slot-selection
        :time="employeeWorkingTime"
        v-model:selected-time="selectedTime"
      />
    </div>

    <!-- Book  Button -->
    <div class="m-3">
      <default-button
        title="Next"
        :disabled="!bookingDetailsSelected"
        @click="goToPayment"
      />
    </div>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import {
  mapGetters,
  mapMutations
} from "vuex";
import moment from "moment-timezone";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import {
  getEmployeeTimeslot,
  getPromotionServiceBranchBookingDateRange
} from "@/services/service";

import DefaultButton from "@/components/button/DefaultButton.vue";
import ExpandableList from "@/components/ExpandableList.vue";
import ExpandableListHeader from "@/components/ExpandableListHeader.vue";
import ExpandableListItem from "@/components/ExpandableListItem.vue";
import ListHeader from "../../../components/ListHeader.vue";
import SmartImage from "@/components/SmartImage.vue";
import SubHeader from "@/components/SubHeader.vue";
import TimeSlotSelection from "@/components/TimeSlotSelection";
import { openModal, closeModal } from "@/store/modal";

import {
  dateTimeFormat
} from "@/common/constant/DateTimeFormat";
import {
  getBranchNameById
} from "@/common/helpers/BaseHelper";
import {
  formatProcessingTime,
  getPromotionHeaderAlt
} from "@/common/helpers/ProductHelper";
import {
  getFormattedNumber
} from "@/common/helpers/PriceHelper.js";
import {
  convertDateToDatabaseFormat,
  convertDateTimeToTimezone,
  formatMinutes,
  convertDateFormat
} from "@/common/helpers/DateTimeHelper.js";

export default {
  components: {
    DefaultButton,
    Datepicker,
    ExpandableList,
    ExpandableListHeader,
    ExpandableListItem,
    ListHeader,
    SubHeader,
    SmartImage,
    TimeSlotSelection,
  },
  name: "SelectAppointmentDate",
  data() {
    return {
      service: null,
      selectedDate: null,
      employeeTimeslots: null,
      selectedEmployee: null,
      selectedTime: null,
      promotionServiceBranchBookingDateRange: [],
    };
  },
  computed: {
    bookingDetailsSelected() {
      return this.selectedDate !== null &&
        this.selectedEmployee !== null &&
        this.selectedTime !== null;
    },
    employeeWorkingTime() {
      if (this.selectedEmployee) {
        const formattedTimeslots = this.selectedEmployee.timeslots.map(
          (timeslot) =>
            convertDateTimeToTimezone({
              dateTime: timeslot,
              outputFormat: dateTimeFormat.TIME_FORMAT,
            })
        );

        return [
          {
            name: "Morning",
            timeslots: formattedTimeslots.filter(
              (timeslot) => timeslot.split(" ")[1] === "AM"
            ),
          },
          {
            name: "Afternoon",
            timeslots: formattedTimeslots.filter(
              (timeslot) => timeslot.split(" ")[1] === "PM"
            ),
          },
        ];
      }
      return null;
    },
    isEmployeeAvailable() {
      if (this.employeeTimeslots) {
        return this.employeeTimeslots.some(
          (employeeTimeslot) => employeeTimeslot.timeslots.length !== 0
        );
      }

      return false;
    },
    ...mapGetters(
      [
        "init",
        "selectedBranch",
        "branchBookingDateRange",
      ]
    ),
  },
  methods: {
    getBranchNameById,
    formatMinutes,
    convertDateFormat,
    goToPayment() {
      if (!this.selectedDate && !this.selectedEmployee && !this.selectedTime)
        return;

      // Save data to sessionstorage
      const saleTicket = {
        branch: this.selectedBranch.id,
        selectedEmployee: this.selectedEmployee,
        selectedTime: this.selectedTime,
        selectedDate: moment(this.selectedDate).format(
          dateTimeFormat.API_DATE_FORMAT
        ),
      };
      sessionStorage.setItem("timeslot", JSON.stringify(saleTicket));

      this.$router.push("/make-appointment/payment");
    },
    async fetchEmployeeTimeSlots() {
      const date = convertDateToDatabaseFormat(this.selectedDate);

      this.showLoading(true);
      try {
        const response = await getEmployeeTimeslot(
          this.service.id,
          date,
          this.selectedBranch.id
        );
        this.employeeTimeslots = response.data;

        // Check if employee and timeslot selected
        this.loadSelectedData();

        this.showLoading(false);
      } catch (error) {
        console.log(error);
        this.showLoading(false);
      }
    },
    initSelectedDate() {
      // If user had selected timeslot before during same session, reuse it else set a default date
      const timeSlot = JSON.parse(sessionStorage.getItem("timeslot"));
      if (timeSlot) {
        this.selectedDate = timeSlot.selectedDate;
      } else {
        this.selectedDate = this.service.promotion
          ? this.promotionServiceBranchBookingDateRange[0]
          : this.branchBookingDateRange[0];
      }
    },
    loadSelectedData() {
      // Selected data not exists
      if (!JSON.parse(sessionStorage.getItem("timeslot"))) return;

      const timeslot = JSON.parse(sessionStorage.getItem("timeslot"));

      // Check if previous selected employee exists in new employeeTimeslots
      const prevSelectedEmployee = this.employeeTimeslots.find((data) =>
        data.id === timeslot.selectedEmployee.id &&
        data.timeslots.length > 0);
      if (prevSelectedEmployee) {
        this.selectedEmployee = prevSelectedEmployee;
      } else {
        return;
      }

      // Combine morning and afternoon timeslots
      const timeslots = [
        ...this.employeeWorkingTime[0].timeslots,
        ...this.employeeWorkingTime[1].timeslots,
      ];
      // Check if previous selected timeslot still available
      this.selectedTime = timeslots.find(
        (data) => data === timeslot.selectedTime
      );
    },
    async setPromotionServiceBranchBookingDateRange() {
      this.showLoading(true);

      try {
        const response = await getPromotionServiceBranchBookingDateRange(
          this.selectedBranch.id,
          this.service.id,
          this.service.promotionId
        );

        this.promotionServiceBranchBookingDateRange = response.data.length ? response.data : ['1970-01-01']; // HERE, HACK
        this.showLoading(false);
      } catch (error) {
        console.log(error);
        this.showLoading(false);
      }
    },
    async initComponent() {
      // Get user selected service from session storage
      this.service = JSON.parse(sessionStorage.getItem("serviceDetails"));

      // if the service is promotion, fetch promotion service branch booking date range
      // since promotion servie booking range won't be based on branch booking date range
      if (this.service.promotion) {
        await this.setPromotionServiceBranchBookingDateRange();
      }
    },
    viewItemDetail(service) {
      openModal(
        {
          id: "expandableListItemDetailModal",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ExpandableListItemDetail.vue"))),
        {
          image: service.imageURL,
          processingTime: service.formatProcessingTime,
          name: service.name,
          description: service.description,
          originalPrice: service.originalPrice,
          salePrice: service.salePrice,
          section: service.sectionName,
          category: service.categoryName,
          promotion: service.promotion,
          hidePrice: this.init.hideServicePrice,
        },
        {},
        [
          {
            text: 'Close',
            clickFn: () => {
              closeModal();
            }
          }
        ]
      );
    },
    getPromotionHeaderAlt,
    formatProcessingTime,
    getFormattedNumber,
    ...mapMutations(["showLoading"]),
  },
  watch: {
    selectedDate() {
      this.fetchEmployeeTimeSlots();
      // reset select employee & time
      this.selectedEmployee = null;
      this.selectedTime = null;
    },
    selectedEmployee(value, oldvalue) {
      if (oldvalue !== null) this.selectedTime = null;
    },
  },
  beforeRouteEnter(to, from, next) {
    const service = JSON.parse(sessionStorage.getItem("serviceDetails"));
    if (!service) {
      next("/error");
    }

    next();
  },
  created() {
    this.initComponent();
    this.initSelectedDate();
  },
};
</script>

<style lang="scss">
.dp__flex_display {
  flex-direction: initial;
}
.dp-custom-menu {
  width: 95%;
  border: 0;
}
.dp-custom-cell {
  border-radius: 50%;
}
.dp__calendar_wrap {
  margin: 0px 10px;
}
.dp__month_year_select {
  color: #4a4a4a;
  font-weight: bold;
}
.dp__calendar_header {
  width: 100%;
}
.dp__calendar_header_item {
  color: #4a4a4a;
  text-align: left;
}
.dp__calendar {
  width: 100%;
}
.dp__calendar_item {
  color: #579eff;
}
.dp__active_date {
  background: #579eff;
}
.dp__cell_disabled {
  color: #979797;
}

.style-avalable-icon {
  color: $text-blue;
  font-size: $large-font-size;
}

.style-vertical-scrolling {
  overflow-y: hidden;
  white-space: nowrap;
}

.style-staff-container {
  padding: 5px 0px 10px;
  flex: 2 0 22%;
  min-width: 95px;
  max-width: 100px;
}
</style>
